import {
   Card,
   Typography,
   Grid,
   TextareaAutosize,
   FormControl,
   RadioGroup,
   FormControlLabel,
} from '@mui/material';
import { useEffect, useRef, useState } from 'react';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import styles from './poojaDetails.module.scss';
import { object, string, ValidationError } from 'yup';
import toast, { Toaster } from 'react-hot-toast';
import ToastSuccess from '../../common/toast/toastSuccess';
import ToastError from '../../common/toast/toastError';
import { apiRequest, catchErrorMessageHandler } from '../../../infrastructure/backendService';
import GridTableComponent from '../../common/GridTableComponent/gridTableTemplate';
import {
   CustomRadioButton,
   CommonButton,
   CustomSelect,
   CustomMenuItem,
   CustomInputLabel,
   CustomTextFeild,
   CommonOutlinedButton,
   CustomRequiredInputLabel,
   CustomLeftIcon,
} from '../../common/StyledComponents/StyledFields';
import { isNil } from 'lodash';
import PoojaDetailsTable from './poojaDetailsTable';
import commonStyles from '../../../App.module.scss';
import ErrorPopup from '../../common/ErrorPopup';
import constants from '../../../config/constants.json';
import SuccessPopup from '../../devotee/donation/successPopup';
import LOCALE from '../../../config/en-us.json';
import apiPath from '../../../config/apiPaths.json';
import { ClipLoader } from 'react-spinners';

interface PoojaDetailsProps {
   handleBack?: () => void;
   selectData?: any;
}
interface IncomeAccount {
   id: string;
   description: string;
}

type ErrorMessageTypes = {
   poojaTitle: string;
   description: string;
   requiredMins: string;
   suggestedDonation: string;
   incomeAccounts: string;
   priestRequired: string;
   forDevotee: string;
};

const PoojaDetails = (props: PoojaDetailsProps) => {
   const { selectData } = props || {};
   const formFields = {
      poojaTitle: '',
      description: '',
      requiredMins: '',
      suggestedDonation: '',
      incomeAccounts: '',
      priestRequired: '',
      forDevotee: '',
   };

   const [errorMessage, setErrorMessage] = useState<ErrorMessageTypes>({
      ...formFields,
   });
   const [formData, setFormData] = useState({
      ...formFields,
   });
   const [incomeAccountsList, setIncomeAccountList] = useState<IncomeAccount[]>([]);

   const [submitLoading, setSubmitLoading] = useState(false);
   const [isOpen, setIsOpen] = useState<boolean>(false);
   const validationSchema = object().shape({
      poojaTitle: string().required('Pooja Title is required'),
      description: string().required('Description is required'),
      requiredMins: string().required('Min  is required'),
      suggestedDonation: string().required('Suggested Donation is required'),
      incomeAccounts: string().required('Income Account is required'),
      priestRequired: string().required('Priest is Required'),
      forDevotee: string().required('forDevotee is Required'),
   });
   useEffect(() => {
      updateData();
   }, [selectData]);

   const updateData = () => {
      setFormData({
         poojaTitle: selectData?.name || '',
         description: selectData?.description || '',
         requiredMins: selectData?.duration || '',
         suggestedDonation: selectData?.suggestedDonation || '',
         incomeAccounts: selectData?.incomeAccount || '',
         priestRequired: selectData?.priestRequired ? 'Yes' : 'No',
         forDevotee: selectData?.forDevotee ? 'Yes' : 'No',
      });
   };

   const handleSave = async (): Promise<void> => {
      const errors: ErrorMessageTypes = {
         poojaTitle: '',
         description: '',
         requiredMins: '',
         suggestedDonation: '',
         incomeAccounts: '',
         priestRequired: '',
         forDevotee: '',
      };

      try {
         // Validate the form data against the validation schema
         await validationSchema.validate(formData, { abortEarly: false });
         // If validation passes, you can submit the form
         setErrorMessage(errors);
         // Call your API or perform other actions here
         saveApiCall();
      } catch (error: any) {
         // Handle validation errors
         if (error instanceof ValidationError) {
            error.inner.forEach((e: any) => {
               if (e.path) {
                  errors[e.path as keyof ErrorMessageTypes] = e.message;
               }
            });
         }
         setErrorMessage(errors);
      }
   };

   const saveApiCall = () => {
      setSubmitLoading(true);
      const priestRequired = formData.priestRequired === 'yes';
      const forDevotee = formData.forDevotee === 'yes';
      const reqBody = {
         name: formData.poojaTitle,
         description: formData.description,
         duration: parseInt(formData.requiredMins),
         suggestedDonation: parseInt(formData.suggestedDonation),
         incomeAccount: formData.incomeAccounts,
         priestRequired: priestRequired,
         forDevotee: forDevotee,
      };
      apiRequest({
         method: 'POST',
         endpoint: apiPath.poojaDetailsAdded,
         includeToken: true,
         body: JSON.stringify(reqBody),
      })
         .then((res) => {
            const successMessage = LOCALE.POOJA_DETAILS_ADDED_CALL;
            toast.custom(
               <SuccessPopup
                  headerMessage="Pooja Details"
                  showMessage={successMessage}
                  isOpen={isOpen}
                  setIsOpen={setIsOpen}
               />,
            );
            setSubmitLoading(false);
         })
         .catch((err) => {
            toast.custom(
               <ErrorPopup
                  showMessage={catchErrorMessageHandler(err)}
                  isOpen={isOpen}
                  setIsOpen={setIsOpen}
               />,
               {
                  duration: constants.TOAST_POPUP_DURATION,
               },
            );
            setSubmitLoading(false);
            console.log(err);
         });
   };
   useEffect(() => {
      apiRequest({
         method: 'GET',
         endpoint: apiPath.poojaDetailsData,
         includeToken: true,
      })
         .then((res) => {
            setIncomeAccountList(res?.data?.content);
         })
         .catch((err) => {
            console.log(err);
            toast.custom(
               <ErrorPopup
                  showMessage={catchErrorMessageHandler(err)}
                  isOpen={isOpen}
                  setIsOpen={setIsOpen}
               />,
               {
                  duration: constants.TOAST_POPUP_DURATION,
               },
            );
         });
   }, []);
   const handleFieldChange = (field: string, value: any) => {
      setFormData({
         ...formData,
         [field]: value,
      });
   };

   const handleUpdate = async (): Promise<void> => {
      const errors: ErrorMessageTypes = { ...formFields };

      try {
         // Validate the form data against the validation schema
         await validationSchema.validate(formData, { abortEarly: false });
         // If validation passes, you can submit the form
         setErrorMessage(errors);
         // Call your API or perform other actions here
         updatePoojaDetailsApiCall();
      } catch (error: any) {
         // Handle validation errors
         if (error instanceof ValidationError) {
            error.inner.forEach((e: any) => {
               if (e.path) {
                  errors[e.path as keyof ErrorMessageTypes] = e.message;
               }
            });
         }
         setErrorMessage(errors);
      }
   };
   const updatePoojaDetailsApiCall = () => {
      setSubmitLoading(true);
      const priestRequired = formData.priestRequired === 'yes';
      const forDevotee = formData.forDevotee === 'yes';
      const reqBody = {
         name: formData.poojaTitle,
         description: formData.description,
         duration: formData.requiredMins,
         suggestedDonation: formData.suggestedDonation,
         incomeAccount: formData.incomeAccounts,
         priestRequired: priestRequired,
         forDevotee: forDevotee,
         id: selectData?.id,
         deleted: false,
      };
      apiRequest({
         method: 'PUT',
         endpoint: `${apiPath.updatePoojaApi}/${selectData?.id}`,
         body: JSON.stringify(reqBody),
         params: {
            deleted: false,
         },
         includeToken: true,
      })
         .then((res) => {
            const successMessage = LOCALE.POOJA_DETAILS_ADDED;
            toast.custom(
               <SuccessPopup
                  headerMessage="Details Update"
                  showMessage={successMessage}
                  isOpen={isOpen}
                  setIsOpen={setIsOpen}
               />,
               {
                  duration: constants.TOAST_POPUP_DURATION,
               },
            );
            setSubmitLoading(false);
         })
         .catch((err) => {
            toast.custom(
               <ErrorPopup
                  showMessage={catchErrorMessageHandler(err)}
                  isOpen={isOpen}
                  setIsOpen={setIsOpen}
               />,
               {
                  duration: constants.TOAST_POPUP_DURATION,
               },
            );
            setSubmitLoading(false);
         });
   };

   const handleReset = () => {
      updateData();
   };
   const handleCancel = () => {
      setFormData({
         ...formFields,
      });
   };
   const handleButtons = () => {
      return (
         <div>
            {!isNil(selectData?.id) ? (
               <>
                  <button className={commonStyles.cancelButton} onClick={handleReset}>
                     Reset
                  </button>
                  <CommonButton className={commonStyles.saveButton} onClick={handleUpdate}>
                     {submitLoading ? (
                        <>
                           <ClipLoader size={20} color="#fff" />
                           <span style={{ marginLeft: '8px' }}>Update</span>
                        </>
                     ) : (
                        'Update'
                     )}
                  </CommonButton>
               </>
            ) : (
               <>
                  <button className={commonStyles.cancelButton} onClick={handleCancel}>
                     Reset
                  </button>
                  <CommonButton className={commonStyles.saveButton} onClick={handleSave}>
                     {submitLoading ? (
                        <>
                           <ClipLoader size={20} color="#fff" />
                           <span style={{ marginLeft: '8px' }}>Add</span>
                        </>
                     ) : (
                        'Add'
                     )}
                  </CommonButton>
               </>
            )}
         </div>
      );
   };
   const handlePriestRequiredChange = (value: any) => {
      // Convert boolean value to string "Yes" or "No"
      const priestRequiredValue = value ? 'Yes' : 'No';

      setFormData({
         ...formData,
         priestRequired: priestRequiredValue,
      });
   };

   const handleDevoteeRequiredChange = (value: any) => {
      setFormData({
         ...formData,
         forDevotee: value,
      });
   };
   const radioButtons = (): React.ReactElement => {
      return (
         <RadioGroup
            className={styles.formGroup}
            value={formData.priestRequired}
            onChange={(event) => handlePriestRequiredChange(event.target.value)}
         >
            <FormControlLabel
               value="Yes"
               control={<CustomRadioButton sx={{ '& .MuiSvgIcon-root': { fontSize: 14 } }} />}
               label={<span style={{ fontSize: 14 }}>Yes</span>}
            />
            <FormControlLabel
               value="No"
               control={<CustomRadioButton sx={{ '& .MuiSvgIcon-root': { fontSize: 14 } }} />}
               label={<span style={{ fontSize: 14 }}>No</span>}
            />
         </RadioGroup>
      );
   };
   const radioButtonsforDevotee = (): React.ReactElement => {
      return (
         <RadioGroup
            className={styles.formGroup}
            value={formData.forDevotee}
            onChange={(event) => handleDevoteeRequiredChange(event.target.value)}
         >
            <FormControlLabel
               value="Yes"
               control={<CustomRadioButton sx={{ '& .MuiSvgIcon-root': { fontSize: 14 } }} />}
               label={<span style={{ fontSize: 14 }}>Yes</span>}
            />
            <FormControlLabel
               value="No"
               control={<CustomRadioButton sx={{ '& .MuiSvgIcon-root': { fontSize: 14 } }} />}
               label={<span style={{ fontSize: 14 }}>No</span>}
            />
         </RadioGroup>
      );
   };

   const incomeAccount = (): React.ReactElement => {
      return (
         <FormControl sx={{ width: '100%' }} className={styles.dropDowns}>
            <CustomSelect
               IconComponent={KeyboardArrowDownIcon}
               id="grouped-select"
               placeholder="Select Nakshatram"
               value={formData.incomeAccounts}
               onChange={(e) => {
                  setErrorMessage({ ...errorMessage, incomeAccounts: '' });
                  handleFieldChange('incomeAccounts', e.target.value);
               }}
               MenuProps={{
                  PaperProps: {
                     sx: {
                        border: '1px solid #F05802',
                        '& ul': {
                           paddingTop: 0,
                           paddingBottom: 0,
                           overflowY: 'auto',
                           minHeight: 'auto',
                           maxHeight: '300px',
                           '&::-webkit-scrollbar': {
                              width: '8px',
                           },
                           '&::-webkit-scrollbar-thumb': {
                              background: '#F3C539',
                              borderRadius: '6px',
                           },
                        },
                     },
                  },
               }}
               className={errorMessage.incomeAccounts ? styles.errorTextField : styles.textField}
            >
               {incomeAccountsList.map((item, index: number) => (
                  <CustomMenuItem key={index} value={item.id}>
                     {item?.id} - {item.description}
                  </CustomMenuItem>
               ))}
            </CustomSelect>
            {errorMessage.incomeAccounts && (
               <Typography sx={{ color: 'red', fontSize: '12px' }}>
                  {errorMessage.incomeAccounts}
               </Typography>
            )}
         </FormControl>
      );
   };
   const handleback = () => {
      props.handleBack?.();
   };
   const handleKeyPress = (e: any) => {
      if (e.key === 'Enter') {
         e.preventDefault();
         if (selectData?.id) {
            handleUpdate();
         } else {
            handleSave();
         }
      }
   };
   const validatePoojatitle = (value: string) => {
      const regex = /^[a-zA-Z0-9\s]*$/;
      return regex.test(value);
   };
   const poojaDetailsFields = () => {
      return (
         <div>
             <Grid sx={{ display: "flex" }} onClick={handleback}>
               <span style={{
                  marginTop: "6px",
                  marginRight: "7px"
               }}>
                  <CustomLeftIcon />
               </span>
            <Typography className={styles.header}>Pooja Details</Typography>
            </Grid>
          
            <form onKeyDown={handleKeyPress}>
               <Card className={styles.cardConatiner}>
                  <Grid sx={{ display: 'flex', flexDirection: 'column', padding: '20px' }}>
                     <Grid container spacing={2} sx={{ display: 'flex', flexDirection: 'column' }}>
                        <Grid xs={12} sm={3} item>
                           {CustomRequiredInputLabel('Pooja Title', true)}
                           <CustomTextFeild
                              fullWidth
                              required
                              value={formData.poojaTitle}
                              onChange={(e) => {
                                 const value = e.target.value;
                                 if (validatePoojatitle(value)) {
                                    setErrorMessage({ ...errorMessage, poojaTitle: '' });
                                    handleFieldChange('poojaTitle', value);
                                 }
                              }}
                              className={
                                 errorMessage.poojaTitle ? styles.errorTextField : styles.textField
                              }
                           />
                           {errorMessage.poojaTitle && (
                              <Typography sx={{ color: 'red', fontSize: '12px' }}>
                                 {errorMessage.poojaTitle}
                              </Typography>
                           )}
                        </Grid>
                        <Grid xs={12} sm={3} item>
                           {CustomRequiredInputLabel('Description', true)}
                           <textarea
                              required
                              value={formData.description}
                              onChange={(e) => {
                                 setErrorMessage({ ...errorMessage, description: '' });
                                 handleFieldChange('description', e.target.value);
                              }}
                              className={
                                 errorMessage.description
                                    ? styles.description
                                    : styles.descriptionText
                              }
                           />
                           {errorMessage.description && (
                              <Typography sx={{ color: 'red', fontSize: '12px' }}>
                                 {errorMessage.description}
                              </Typography>
                           )}
                        </Grid>
                     </Grid>
                     <Grid container spacing={2} sx={{ marginTop: '0px' }}>
                        <Grid item xs={12} sm={3}>
                           {CustomRequiredInputLabel('Time Required (Mins)', true)}
                           <CustomTextFeild
                              fullWidth
                              required
                              value={formData.requiredMins}
                              onChange={(e) => {
                                 const numericValue = e.target.value.replace(/[^0-9]/g, ''); // Only allow numeric values
                                 setErrorMessage({ ...errorMessage, requiredMins: '' });
                                 handleFieldChange('requiredMins', numericValue);
                               }}
                              className={
                                 errorMessage.requiredMins
                                    ? styles.errorTextField
                                    : styles.textField
                              }
                           />
                           {errorMessage.requiredMins && (
                              <Typography sx={{ color: 'red', fontSize: '12px' }}>
                                 {errorMessage.requiredMins}
                              </Typography>
                           )}
                        </Grid>
                        <Grid item xs={12} sm={3}>
                           {CustomRequiredInputLabel('Suggested Donation', true)}
                           <CustomTextFeild
                              fullWidth
                              required
                              value={formData.suggestedDonation}
                              onChange={(e) => {
                                 const numericValue = e.target.value.replace(/[^0-9]/g, ''); // Only allow numeric values
                                 setErrorMessage({ ...errorMessage, requiredMins: '' });
                                 handleFieldChange('suggestedDonation', numericValue);
                               }}
                              className={
                                 errorMessage.suggestedDonation
                                    ? styles.errorTextField
                                    : styles.textField
                              }
                           />
                           {errorMessage.suggestedDonation && (
                              <Typography sx={{ color: 'red', fontSize: '12px' }}>
                                 {errorMessage.suggestedDonation}
                              </Typography>
                           )}
                        </Grid>
                     </Grid>
                     <Grid container spacing={2} sx={{ marginTop: '0px' }}>
                        <Grid item xs={12} sm={3}>
                           {CustomRequiredInputLabel('Priest Required?', true)}
                           {radioButtons()}
                        </Grid>
                        <Grid item xs={12} sm={3}>
                           {CustomRequiredInputLabel('For Devotee', true)}
                           {radioButtonsforDevotee()}
                        </Grid>
                     </Grid>
                     <Grid container spacing={2} sx={{ marginTop: '0px' }}>
                        <Grid item xs={12} sm={3}>
                           {CustomRequiredInputLabel('Income Account', true)}
                           {incomeAccount()}
                        </Grid>
                     </Grid>
                  </Grid>
               </Card>
            </form>
         </div>
      );
   };

   return (
      <div className={styles.mainContainer}>
         <Toaster />
         {poojaDetailsFields()}
         {handleButtons()}
      </div>
   );
};

export default PoojaDetails;
